// VisitAssistantActionContext.tsx
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AssistantAction } from 'src/sections/@dashboard/visit/utils/assistantActions';

interface VisitAssistantActionContextType {
  patientNotesDraft: string;
  setPatientNotesDraft: (value: string) => void;
  internalNotesDraft: string;
  setInternalNotesDraft: (value: string) => void;
  registerAssistantActionHandler: (
    handler: ((action: AssistantAction) => void) | null,
  ) => void;
  dispatchAssistantAction: (action: AssistantAction) => void;
}

const noop = () => {};

// Default value is a safe no-op so consuming outside a provider never crashes.
const VisitAssistantActionContext =
  createContext<VisitAssistantActionContextType>({
    patientNotesDraft: '',
    setPatientNotesDraft: noop,
    internalNotesDraft: '',
    setInternalNotesDraft: noop,
    registerAssistantActionHandler: noop,
    dispatchAssistantAction: noop,
  });

export function VisitAssistantActionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [patientNotesDraft, setPatientNotesDraft] = useState('');
  const [internalNotesDraft, setInternalNotesDraft] = useState('');
  const handlerRef = useRef<((action: AssistantAction) => void) | null>(null);

  const registerAssistantActionHandler = useCallback(
    (handler: ((action: AssistantAction) => void) | null) => {
      handlerRef.current = handler;
    },
    [],
  );

  const dispatchAssistantAction = useCallback((action: AssistantAction) => {
    handlerRef.current?.(action);
  }, []);

  const value = useMemo(
    () => ({
      patientNotesDraft,
      setPatientNotesDraft,
      internalNotesDraft,
      setInternalNotesDraft,
      registerAssistantActionHandler,
      dispatchAssistantAction,
    }),
    [
      patientNotesDraft,
      internalNotesDraft,
      registerAssistantActionHandler,
      dispatchAssistantAction,
    ],
  );

  return (
    <VisitAssistantActionContext.Provider value={value}>
      {children}
    </VisitAssistantActionContext.Provider>
  );
}

export function useVisitAssistantAction() {
  return useContext(VisitAssistantActionContext);
}
